class Footer {
  constructor() {
    this.footer = document.querySelector("footer");
    this.mql = window.matchMedia("(min-width: 750px)");
    this.localisationFormOpen = () =>
      this.footer.querySelector('.disclosure__button[aria-expanded="true"]');
    this.elements = {
      newsletter: this.footer.querySelector(".footer-block--newsletter"),
      menus: this.footer.querySelectorAll(".footer__list-menu"),
      localization: this.footer.querySelector("localization-form"),
      address: this.footer.querySelector(".footer-block--address"),
      captcha: this.footer.querySelector("#alt-shop-hcaptcha-badge-container"),
    };

    this.childHeightArray = [
      this.elements.newsletter.clientHeight,
      ...Array.from(this.elements.menus).map((menu) => menu.clientHeight),
      this.elements.localization.clientHeight,
      this.elements.address.clientHeight,
    ];

    this.updateChildHeights();
    this.setupIntersectionObserver();

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateChildHeights();
      }, 150);
    });
    this.footer.addEventListener("click", this.onClick.bind(this));
  }

  updateChildHeights() {
    this.childHeightArray = [
      this.elements.newsletter.clientHeight,
      ...Array.from(this.elements.menus).map((menu) => menu.clientHeight),
      this.elements.localization.clientHeight,
      this.elements.address.clientHeight,
    ];
  }

  setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      this.onIntersection.bind(this),
      {
        rootMargin: "0px", // Reduced margin for better performance
        threshold: [0, 1], // Only entry/exit points
      }
    );

    if (!this.localisationFormOpen()) {
      this.intersectionObserver.observe(this.footer);
    }

    const initialProgress =
      this.footer.getBoundingClientRect().top > vh ? 1 : 0;

    if (this.mql.matches) this.applyDesktopParallax(initialProgress);
    else this.applyMobileParallax(initialProgress);
  }

  onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.isActive) {
        this.startParallax();
      } else if (!entry.isIntersecting && this.isActive) {
        this.stopParallax();
      }
    });
  }

  startParallax() {
    if (this.isActive) return;
    this.isActive = true;
    this.footer.style.overflow = "hidden";
    this.updateParallax();
  }

  stopParallax() {
    if (!this.isActive) return;
    this.isActive = false;

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.lastProgress = -1;
  }

  updateParallax() {
    if (!this.isActive) return;

    // Check conditions that would disable parallax

    if (this.localisationFormOpen()) {
      this.removeParallax();
      return;
    }

    // Calculate intersection progress
    const rect = this.footer.getBoundingClientRect();

    // Calculate progress (0 = fully visible, 1 = just entering viewport)
    const progress = Math.max(
      0,
      Math.min(1, (rect.height + rect.top - vh) / rect.height)
    );

    document.documentElement.style.setProperty(
      "--footer-mask-progress",
      1 - (Math.max(0.5, 1 - progress) * 2 - 1)
    );

    const easedProgress = progress ** 2;

    // Only update if progress has meaningfully changed
    if (Math.abs(easedProgress - this.lastProgress) < 0.001) {
      this.rafId = requestAnimationFrame(() => this.updateParallax());
      return;
    }
    this.lastProgress = easedProgress;

    // Apply transforms based on screen size
    if (this.mql.matches) this.applyDesktopParallax(easedProgress);
    else this.applyMobileParallax(easedProgress);

    this.rafId = requestAnimationFrame(() => this.updateParallax());
  }

  applyDesktopParallax(progress) {
    const translateY =
      progress * (this.footer.firstElementChild.clientHeight - 1);
    this.footer.firstElementChild.style.transform = `translate3d(0, ${translateY}px, 0)`;
  }

  applyMobileParallax(progress) {
    // Pre-calculate common values
    const heights = this.childHeightArray;
    const totalHeight = heights.reduce((sum, height) => sum + height, 0) - 1;

    // Apply transforms using translate3d for hardware acceleration
    const transforms = [
      { element: this.elements.newsletter, offset: totalHeight },
      { element: this.elements.captcha, offset: totalHeight },
      { element: this.elements.menus[0], offset: totalHeight - heights[0] },
      {
        element: this.elements.menus[1],
        offset: totalHeight - heights[0] - heights[1],
      },
      {
        element: this.elements.localization,
        offset: heights[3] + heights[4] - 1,
      },
      { element: this.elements.address, offset: heights[4] - 1 },
    ];

    transforms.forEach(({ element, offset }) => {
      const translateY = progress * offset;
      element.style.transform = `translate3d(0, ${translateY}px, 0)`;
    });

    // Special handling for localization element
    this.elements.localization.style.zIndex = 4;
    this.elements.localization.style.transitionDelay = "0ms";
  }

  removeParallax() {
    this.stopParallax();

    // Remove all transform and style properties
    const propertiesToRemove = ["overflow"];
    const elementPropertiesToRemove = [
      "transform",
      "transition",
      "transition-delay",
      "z-index",
    ];

    // Clean up footer styles
    propertiesToRemove.forEach((prop) => {
      this.footer.style.removeProperty(prop);
    });

    // Clean up element styles
    const elementsToClean = [
      this.footer.firstElementChild,
      this.elements.newsletter,
      this.elements.localization,
      this.elements.address,
      this.elements.captcha,
      ...this.elements.menus,
    ];

    elementsToClean.forEach((element) => {
      if (element) {
        elementPropertiesToRemove.forEach((prop) => {
          if (
            element !== this.elements.localization ||
            prop !== "transform" ||
            !this.localisationFormOpen()
          )
            element.style.removeProperty(prop);
        });
      }
    });

    document.documentElement.style.setProperty("--footer-mask-progress", "1");
  }

  onClick(e) {
    if (
      !e.target.classList.contains("disclosure__button") &&
      !e.target.closest(".footer-block__newsletter")
    )
      window.scrollTo({
        top: document.documentElement.scrollHeight - vh,
        behavior: "smooth",
      });
    else if (this.localisationFormOpen()) this.removeParallax();

    if (
      document.activeElement.closest(".footer-block__newsletter") &&
      this.elements.newsletter.querySelector(".field__input:focus")
    ) {
      this.removeParallax();
      this.elements.newsletter
        .querySelector(".field__input:focus")
        .addEventListener(
          "blur",
          function (e) {
            setTimeout(
              function () {
                console.log(this);

                this.startParallax.bind(this)();
              }.bind(this),
              800
            );
          }.bind(this)
        );
      return;
    }
  }
}

const footer = new Footer();
